import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
// import {
//   Dialog,
//   DialogContent,
//   DialogHeader,
//   DialogTitle,
//   DialogTrigger,
// } from '@/components/ui/dialog'
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableHead,
//   TableHeader,
//   TableRow,
// } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'
import { Plus, X } from 'lucide-react'
import { useUser } from '@/hooks/useUser'
// import { API_URL } from '@/config/constants'
// import { useSuperAdminStore } from '@/store/superAdminStore'
import { useTranslation } from 'react-i18next'
import { EstablishmentsSuperAdminTour } from '@/tour/superAdminTour/establishmentsSuperAdmintour'
import {
  useEstablishments,
  type Establishment,
  type EstablishmentFormValues,
  type EstablishmentApiData,
} from '@/hooks/useEstablishments'
import EstablishmentServiceCard from '@/components/card/EstablishmentServiceCard'
import FileUploader from '@/components/shared/FileUploader'
import CMSTemplateSelector from '@/components/schoolTemplateEditor/CMSTemplateSelector'
import GalleryImagesUploader from '@/components/schoolTemplateEditor/GalleryImagesUploader'
import SocialLinksEditor from '@/components/schoolTemplateEditor/SocialLinksEditor'
import StatsEditor from '@/components/schoolTemplateEditor/StatsEditor'
import { ScrollArea } from '@/components/ui/scroll-area'
import { guardSuperAdminOnly } from '@/lib/auth/routeGuards'

export const Route = createFileRoute('/super_admin/etablisments')({
  beforeLoad: guardSuperAdminOnly,
  component: RouteComponent,
})

function EstablishmentForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  isSubmitting = false,
}: {
  initialData?: Establishment
  onSubmit: (data: EstablishmentFormValues) => void
  onCancel: () => void
  submitLabel?: string
  isSubmitting?: boolean
}) {
  const [newService, setNewService] = useState('')

  const form = useForm({
    defaultValues: {
      name: initialData?.name || '',
      address: initialData?.address || '',
      logo: initialData?.logo || '',
      url: initialData?.url || '',
      CMSContent: initialData?.CMSContent || '',
      services: initialData?.services || [],
      isActive: initialData?.isActive ?? true,
      description: initialData?.description || '',
      heroImage: initialData?.heroImage || '',
      galleryImages: initialData?.galleryImages || [],
      contactEmail: initialData?.contactEmail || '',
      contactPhone: initialData?.contactPhone || '',
      socialLinks: initialData?.socialLinks || {
        facebook: '',
        twitter: '',
        instagram: '',
        linkedin: '',
      },
      stats: initialData?.stats || {
        students: 0,
        teachers: 0,
        courses: 0,
        awards: 0,
      },
    },
    onSubmit: async ({ value }) => {
      onSubmit(value)
    },
  })

  const addService = () => {
    if (newService.trim() !== '') {
      const currentServices = form.state.values.services || []
      form.setFieldValue('services', [...currentServices, newService.trim()])
      setNewService('')
    }
  }

  const removeService = (index: number) => {
    const currentServices = form.state.values.services || []
    form.setFieldValue(
      'services',
      currentServices.filter((_, i) => i !== index)
    )
  }
  const { t } = useTranslation()

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          {initialData
            ? t('routes.super-admin-folder.etablisments.editTitle')
            : t('routes.super-admin-folder.etablisments.pageTitle')}
        </CardTitle>
        <CardDescription>
          {initialData
            ? 'Update the establishment information'
            : 'Enter the details for the new establishment'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field */}
            <form.Field
              name="name"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return 'Name is required'
                  return undefined
                },
              }}
            >
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t(
                      'routes.super-admin-folder.etablisments.form.fields.name.label'
                    )}
                  </Label>
                  <Input
                    id="name"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Enter establishment name"
                  />
                  {field.state.meta.errors && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Address Field */}
            <form.Field
              name="address"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return 'Address is required'
                  return undefined
                },
              }}
            >
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="address">
                    {t(
                      'routes.super-admin-folder.etablisments.form.fields.address.label'
                    )}
                  </Label>
                  <Input
                    id="address"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Enter establishment address"
                  />
                  {field.state.meta.errors && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Logo URL Field */}
            <form.Field name="logo">
              {(field) => (
                <FileUploader
                  label={t(
                    'routes.super-admin-folder.etablisments.form.fields.logo.label'
                  )}
                  defaultPreview={field.state.value}
                  onFileUploaded={(url) => field.handleChange(url)}
                  isAvatar={false}
                />
              )}
            </form.Field>

            {/* Website URL Field */}
            <form.Field name="url">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="url">
                    {t(
                      'routes.super-admin-folder.etablisments.form.fields.url.label'
                    )}
                  </Label>
                  <Input
                    id="url"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Enter website URL"
                  />
                  {field.state.meta.errors && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          </div>

          {/* CMS Content Field with Template Selector */}
          <form.Field name="CMSContent">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="CMSContent">CMS Content</Label>
                <CMSTemplateSelector
                  value={field.state.value || ''}
                  onChange={(value) => field.handleChange(value)}
                  onCustomChange={(value) => field.handleChange(value)}
                />
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Description Field */}
          <form.Field name="description">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="description">
                  {t(
                    'routes.super-admin-folder.etablisments.form.fields.description.label'
                  )}
                </Label>
                <Textarea
                  id="description"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Enter a description of your school"
                  className="min-h-32"
                />
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Hero Image Field */}
            <form.Field name="heroImage">
              {(field) => (
                <FileUploader
                  label="Hero Image"
                  defaultPreview={field.state.value}
                  onFileUploaded={(url) => field.handleChange(url)}
                  isAvatar={false}
                />
              )}
            </form.Field>

            {/* Gallery Images Field */}
            <form.Field name="galleryImages">
              {(field) => (
                <GalleryImagesUploader
                  value={field.state.value || []}
                  onChange={(urls) => field.handleChange(urls)}
                  maxImages={5}
                />
              )}
            </form.Field>

            {/* Contact Email Field */}
            <form.Field name="contactEmail">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">
                    {t(
                      'routes.super-admin-folder.etablisments.form.fields.contactEmail.label'
                    )}
                  </Label>
                  <Input
                    id="contactEmail"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Enter contact email"
                  />
                  {field.state.meta.errors && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Contact Phone Field */}
            <form.Field name="contactPhone">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="contactPhone">
                    {t(
                      'routes.super-admin-folder.etablisments.form.fields.contactPhone.label'
                    )}
                  </Label>
                  <Input
                    id="contactPhone"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Enter contact phone"
                  />
                  {field.state.meta.errors && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          </div>

          {/* Social Links Field */}
          <form.Field name="socialLinks">
            {(field) => (
              <SocialLinksEditor
                value={field.state.value || {}}
                onChange={(links) => field.handleChange(links)}
              />
            )}
          </form.Field>

          {/* Stats Field */}
          <form.Field name="stats">
            {(field) => (
              <StatsEditor
                value={field.state.value || {}}
                onChange={(stats) => field.handleChange(stats)}
              />
            )}
          </form.Field>

          {/* Services Field */}
          <form.Field name="services">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="services">
                  {t(
                    'routes.super-admin-folder.etablisments.form.fields.services.label'
                  )}
                </Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    id="newService"
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder="Add a service"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addService()
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={addService}
                    variant="outline"
                    size="icon"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="h-24 w-full border rounded-md p-2">
                  <div className="flex flex-wrap gap-2">
                    {field.state.value?.map(
                      (service: string, index: number) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {service}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => removeService(index)}
                          />
                        </Badge>
                      )
                    )}
                  </div>
                </ScrollArea>
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Active Status Field */}
          <form.Field name="isActive">
            {(field) => (
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
                <Label htmlFor="isActive">Active Status</Label>
              </div>
            )}
          </form.Field>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function RouteComponent() {
  const { t } = useTranslation()
  const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null)
  const [, setIsEditing] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const queryClient = useQueryClient()
  
  // Use the new user store instead of the local useAuth hook
  const { user, isAuthenticated } = useUser()

  // Debug tour state
  // const { globalTour } = useSuperAdminStore()

  const {
    establishments,
    isLoading,
    isError,
    error,
    createEstablishment,
    updateEstablishment,
    isCreating: isCreatingEstablishment,
    isUpdating: isUpdatingEstablishment,
    // We don't need fetchEstablishmentById here anymore
  } = useEstablishments()

  // Debug tour state
  useEffect(() => {
// console.logEstablishments page mounted')
  }, [])

  // Debug user data
  useEffect(() => {
// console.logUser data:', user)
// console.logIs authenticated:', isAuthenticated)
  }, [user, isAuthenticated])

  const handleCreateSubmit = async (data: EstablishmentFormValues) => {
    try {
      // Check if the user already has an establishment
      if (establishments.length >= 1) {
        toast.error('You can only create one establishment')
        return
      }

  // console.logCreating establishment with data:', data)
  // console.logUser ID:', user?.id)

      // Ensure user ID is available
      if (!user?.id) {
        toast.error('User ID is required to create an establishment')
        return
      }

      // Match exactly what works in Postman
      const establishmentData: EstablishmentApiData = {
        name: data.name,
        address: data.address,
        logo: data.logo || undefined,
        url: data.url || undefined,
        CMSContent: data.CMSContent,
        description: data.description,
        heroImage: data.heroImage,
        galleryImages: data.galleryImages,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        socialLinks: data.socialLinks,
        stats: data.stats,
        services: data.services,
        isActive: data.isActive !== undefined ? data.isActive : true,
        superAdminId: user.id,
      }

      createEstablishment(establishmentData, {
        onSuccess: () => {
      // console.logEstablishment created successfully:', result)
          queryClient.invalidateQueries({ queryKey: ['establishments'] })
          toast.success('Establishment created successfully')
        },
        onError: (err: Error) => {
          // console.error('Error creating establishment:', err)
          toast.error(err.message || 'Failed to create establishment')
        },
      })
    } catch (err) {
      // console.error('Error in handleCreateSubmit:', err)
      toast.error(err instanceof Error ? err.message : 'Unknown error occurred')
    }
  }

  const handleEditSubmit = async (data: EstablishmentFormValues) => {
    if (!selectedEstablishment) return

    try {
  // console.logUpdating establishment with ID:', selectedEstablishment.id)
  // console.logUpdate data:', data)

      // Ensure user ID is available
      if (!user?.id) {
        toast.error('User ID is required to update an establishment')
        return
      }

      // Match exactly what works in Postman
      const establishmentData: EstablishmentApiData = {
        name: data.name,
        address: data.address,
        logo: data.logo || undefined,
        url: data.url || undefined,
        CMSContent: data.CMSContent,
        description: data.description,
        heroImage: data.heroImage,
        galleryImages: data.galleryImages,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        socialLinks: data.socialLinks,
        stats: data.stats,
        services: data.services,
        isActive: data.isActive !== undefined ? data.isActive : true,
        superAdminId: user?.id || '',
      }

      updateEstablishment(
        { id: selectedEstablishment.id, data: establishmentData },
        {
          onSuccess: () => {
        // console.logEstablishment updated successfully:', result)
            queryClient.invalidateQueries({ queryKey: ['establishments'] })
            toast.success('Establishment updated successfully')
          },
          onError: (err: Error) => {
            // console.error('Error updating establishment:', err)
            toast.error(err.message || 'Failed to update establishment')
          },
        }
      )
    } catch (err) {
      // console.error('Error in handleEditSubmit:', err)
      toast.error(err instanceof Error ? err.message : 'Unknown error occurred')
    }
  }

  // const navigate = useNavigate() // Commented out to fix TypeScript error

  // Removed unused function to fix TypeScript error

  const handleCancel = () => {
    setSelectedEstablishment(null)
    setIsCreating(false)
    setIsEditing(false)
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return (
      <div className="p-8">
        <div className="text-center p-8 border rounded-lg">
          <p className="text-red-500 mb-4">
            {t('routes.super-admin-folder.etablisments.authRequired.title')}
          </p>
          <p className="text-gray-500">
            {t('routes.super-admin-folder.etablisments.authRequired.message')}
          </p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        {t('routes.super-admin-folder.etablisments.loading')}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            {t('routes.super-admin-folder.etablisments.pageTitle')}
          </h1>
          <Button onClick={() => setIsCreating(true)}>
            {t('routes.super-admin-folder.etablisments.createButton')}
          </Button>
        </div>
        <div className="text-center p-8 border rounded-lg">
          <p className="text-red-500 mb-4">
            {error instanceof Error
              ? error.message
              : 'Failed to load establishments'}
          </p>
          <p className="text-gray-500 mb-4">
            {t('routes.super-admin-folder.etablisments.errorDescription')}
          </p>
        </div>
      </div>
    )
  }

  if (selectedEstablishment) {
    return (
      <div className="p-8">
        <EstablishmentForm
          initialData={selectedEstablishment}
          onSubmit={handleEditSubmit}
          onCancel={handleCancel}
          submitLabel="Update"
          isSubmitting={isUpdatingEstablishment}
        />
      </div>
    )
  }

  if (isCreating) {
    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            {t('routes.super-admin-folder.etablisments.createButton')}
          </h1>
        </div>
        <EstablishmentForm
          onSubmit={handleCreateSubmit}
          onCancel={handleCancel}
          submitLabel="Create"
          isSubmitting={isCreatingEstablishment}
        />
      </div>
    )
  }

  return (
    <div className="p-8">
      <EstablishmentsSuperAdminTour />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold establishments-header">
          {t('routes.super-admin-folder.etablisments.pageTitle')}
        </h1>
        {/* <Button onClick={() => setIsCreating(true)}>
          {t('routes.super-admin-folder.etablisments.createButton')}
        </Button> */}
      </div>

      {establishments.length === 0 ? (
        <div className="text-center p-8 border rounded-lg">
          <p className="text-gray-500">
            {t('routes.super-admin-folder.etablisments.emptyState.message')}
          </p>
          <p className="text-gray-500 mt-2">
            {t('routes.super-admin-folder.etablisments.emptyState.description')}
          </p>
          <Button 
            onClick={() => setIsCreating(true)} 
            className="mt-4"
            variant="default"
          >
            {t('routes.super-admin-folder.etablisments.createButton')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {establishments.map((establishment: Establishment) => (
            <div key={establishment.id} className="establishment-card">
              <EstablishmentServiceCard
                establishment={{
                  ...establishment,
                  createdAt: establishment.createdAt
                    ? establishment.createdAt.toString()
                    : new Date().toISOString(),
                  updatedAt: establishment.updatedAt
                    ? establishment.updatedAt.toString()
                    : new Date().toISOString(),
                  createdBy: establishment.createdBy || 'system',
                  updatedBy: establishment.updatedBy || 'system',
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
